import React, { useState, useEffect } from 'react';
import { TimerMode } from '../types';
import { categorizeActivity } from '../services/geminiService';

interface LogModalProps {
  isOpen: boolean;
  onClose: () => void; 
  onSubmit: (notes: string, category: string) => void;
  onDiscard?: () => void;
  mode: TimerMode;
  defaultNotes?: string;
  defaultCategory?: string;
  durationMinutes: number;
}

const LogModal: React.FC<LogModalProps> = ({ 
  isOpen, 
  onSubmit, 
  onDiscard, 
  mode, 
  durationMinutes, 
  defaultNotes = '', 
  defaultCategory = '' 
}) => {
  const [notes, setNotes] = useState(defaultNotes);
  const [category, setCategory] = useState(defaultCategory);
  const [isCategorizing, setIsCategorizing] = useState(false);

  useEffect(() => {
    if (isOpen) {
        setNotes(defaultNotes);
        setCategory(defaultCategory);
    }
  }, [isOpen, defaultNotes, defaultCategory]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (notes.length < 3) return;

    let finalCategory = category;

    // Only run AI if no category is manually provided
    if (!finalCategory.trim()) {
        setIsCategorizing(true);
        // Auto-categorize on submit
        finalCategory = await categorizeActivity(notes);
        setIsCategorizing(false);
    }
    
    onSubmit(notes, finalCategory);
    setNotes('');
    setCategory('');
  };

  const durationDisplay = durationMinutes < 1 
    ? "less than a minute" 
    : `${durationMinutes.toFixed(0)} minutes`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 transform transition-all animate-in fade-in zoom-in duration-200">
        <h2 className="text-2xl font-bold text-slate-800 mb-2">
            {mode === 'focus' ? 'Focus Session Complete!' : 'Break Over!'}
        </h2>
        <p className="text-slate-500 mb-6">
            You just spent {durationDisplay} {mode === 'focus' ? 'focusing' : 'resting'}. 
            {mode === 'focus' ? ' What did you accomplish?' : ' How did you recharge?'}
        </p>

        <form onSubmit={handleSubmit}>
            <div className="mb-4">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Activity Notes</label>
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={mode === 'focus' ? "I worked on the quarterly report..." : "I took a walk and drank water..."}
                    className="w-full h-32 p-4 rounded-xl border border-slate-200 bg-slate-50 text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none"
                    maxLength={500}
                    required
                    minLength={3}
                    autoFocus
                />
            </div>

            <div className="mb-6">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Category (Optional)</label>
                <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="e.g. Work, Study, Health (Leave empty for AI)"
                    className="w-full p-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
            </div>
            
            <div className="flex flex-col gap-3">
                <button
                    type="submit"
                    disabled={isCategorizing || notes.length < 3}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                    {isCategorizing ? (
                        <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Analyzing...
                        </>
                    ) : (
                        'Save & Continue'
                    )}
                </button>
                
                {onDiscard && (
                    <button
                        type="button"
                        onClick={onDiscard}
                        className="w-full text-slate-400 hover:text-red-500 text-sm font-medium py-2 transition-colors"
                    >
                        Discard Entry
                    </button>
                )}
            </div>
        </form>
      </div>
    </div>
  );
};

export default LogModal;